import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Mail, Send, Shield, Sparkles, Heart, Users, Zap, Globe, Hand, Handshake } from "lucide-react";

const NewsletterCTA = () => {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubscribing(true);

    // Simulate subscription
    setTimeout(() => {
      setIsSubscribing(false);
      toast({
        title: "Welcome to Our Circle! 🌸",
        description:
          "Thank you for subscribing. You'll receive mindful insights and guided practices.",
      });
      setEmail("");
    }, 1500);
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section
      className="py-24 relative overflow-hidden bg-white"
    >
      {/* Background Pattern Layers */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Enhanced Mandala Pattern */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at center, transparent 40%, rgba(230, 243, 240, 0.25) 42%, transparent 44%),
            radial-gradient(circle at center, rgba(230, 239, 246, 0.2) 20%, transparent 22%)
          `,
          backgroundSize: '160px 160px, 120px 120px',
          backgroundPosition: '0 0, 60px 60px'
        }}></div>

        {/* Enhanced Geometric Lines */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(60deg, rgba(230, 243, 240, 0.15) 25%, transparent 25.5%),
            linear-gradient(-60deg, rgba(230, 239, 246, 0.15) 25%, transparent 25.5%)
          `,
          backgroundSize: '100px 173.20px'
        }}></div>

        {/* Enhanced Grid */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            repeating-linear-gradient(45deg,
              rgba(137, 184, 194, 0.08) 0px,
              rgba(137, 184, 194, 0.08) 1px,
              transparent 1px,
              transparent 20px
            )
          `,
          backgroundSize: '50px 50px'
        }}></div>
        
        {/* Enhanced Floating Elements */}
        <div className="absolute top-1/4 left-1/6 w-48 h-48 rounded-full bg-gradient-to-br from-[#E6F3F0]/20 to-transparent animate-float"></div>
        <div className="absolute bottom-1/3 right-1/4 w-40 h-40 rounded-full bg-gradient-to-tl from-[#E6EFF6]/20 to-transparent" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Content Container */}
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="max-w-6xl mx-auto"
        >
          {/* Update text colors for white background */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            {/* <div className="inline-flex items-center justify-center mb-8">
              <motion.div
                className="w-24 h-24 rounded-3xl bg-[#E6F3F0] flex items-center justify-center shadow-lg"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <Handshake className="w-12 h-12 text-forest-green" />
              </motion.div>
            </div> */}

            <h2 className="font-heading text-4xl lg:text-6xl font-bold text-forest-green mb-6">
              Stay Connected with{" "}
              <span className="text-soft-saffron">Cittasya</span>
            </h2>
            <p className="text-xl lg:text-2xl text-[#4A6670] mb-12 leading-relaxed max-w-4xl mx-auto">
              Join our community of conscious seekers and receive
              insights, guided practices and wellness wisdom delivered
              mindfully.&nbsp;
            </p>
          </motion.div>

          {/* Update Features Grid colors */}
          <motion.div
            variants={itemVariants}
            className="grid md:grid-cols-3 gap-8 mb-16"
          >
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-[#E6F3F0] flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-forest-green" />
              </div>
              <h3 className="font-heading text-lg font-bold text-forest-green mb-2">
                Mindful Content
              </h3>
              <p className="text-[#4A6670] text-sm">
                Curated insights and practices delivered with intention
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-[#E6EFF6] flex items-center justify-center mb-4">
                <Heart className="w-8 h-8 text-forest-green" />
              </div>
              <h3 className="font-heading text-lg font-bold text-forest-green mb-2">
                Habit Coach
              </h3>
              <p className="text-[#4A6670] text-sm">
                Structure and discipline to shape a consistent, lasting habit
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-[#E6F3F0] flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-forest-green" />
              </div>
              <h3 className="font-heading text-lg font-bold text-forest-green mb-2">
                Sacred Sanctuary
              </h3>
              <p className="text-[#4A6670] text-sm">
                Your personal companion for growth and transformation
              </p>
            </div>
          </motion.div>

          {/* Update Privacy Notice colors */}
          {/* <motion.div variants={itemVariants} className="text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E6EFF6] border border-[#7AA6B6]/20">
              <Shield className="w-5 h-5 text-forest-green" />
              <p className="text-[#4A6670] text-sm font-medium">
                We respect your privacy and send emails mindfully. Unsubscribe
                anytime with love.
              </p>
            </div>
          </motion.div> */}
        </motion.div>
      </div>
    </section>
  );
};

export default NewsletterCTA;
